import { faEye, faEyeSlash } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Accordion, AccordionSummary, Box, Stack, Tooltip, Typography } from '@vapor/material'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useAdditionalServicesQuery } from 'src/api/api.generated'
import { useTenant } from 'src/app/hooks/context/context.hook'
import { ReservationNewAdditionalService, ReservationCartRow } from '../../form/reservation-new.type'
import AdditionalServiceCell from './additional-service-cell'
import { HicIconButton } from 'src/app/components/hic-vapor/button/icon-button'
import useBreakpoints from 'src/app/hooks/breakpoints/use-breakpoints'

type AdditionalServicesAccordionProps = {
  additionalServices: ReservationNewAdditionalService[]
  propertyId: string
  cartRows: ReservationCartRow[]
  updateCartRows: (updatedRows: ReservationCartRow[]) => void
}

const AdditionalServicesAccordion = (props: AdditionalServicesAccordionProps) => {
  const { id: tenantId } = useTenant()
  const { cartRows, updateCartRows } = props
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const [disableAccordion, setDisableAccordion] = useState(false)
  const { sm } = useBreakpoints()

  const { data: additionalServicesResult, loading: loadingAdditionalServices } = useAdditionalServicesQuery({
    variables: {
      filter: {
        tenantId: {
          eq: tenantId,
        },
        propertyId: {
          eq: props.propertyId,
        },
      },
    },
  })

  useEffect(() => setDisableAccordion(props.additionalServices.length <= 0), [props.additionalServices.length])

  return (
    <Accordion disabled={disableAccordion} expanded={open && !disableAccordion} style={{ boxShadow: 'none' }}>
      <AccordionSummary
        sx={{
          backgroundColor: 'var(--AzureishWhite) !important',
          '& .MuiAccordionSummary-content': {
            paddingRight: '0px !important',
          },
        }}
        onClick={() => {
          if (!disableAccordion) setOpen(!open)
        }}
      >
        <Stack direction="row" spacing={1} justifyContent="space-between" alignItems="center" width="100%">
          {disableAccordion === false && (
            <Tooltip arrow title={!open ? t('pms.booking.reservation_new.add_service_accordion.tooltip') : ''}>
              <span>
                <Typography variant="titleSmall" sx={{ border: '1px solid red', display: 'contents' }}>
                  <b>{t('pms.booking.reservation_new.additional_services')}</b>
                </Typography>
              </span>
            </Tooltip>
          )}
          {disableAccordion && (
            <Stack direction="column" justifyContent="center" alignItems="center" width={'100%'} py={2}>
              <Typography variant="body">
                <b>{t('pms.booking.reservation_new.additional_services_empty_title')}</b>
              </Typography>
              {sm && <Typography variant="body">{t('pms.booking.reservation_new.additional_services_empty_subtitle')}</Typography>}
            </Stack>
          )}
          <HicIconButton
            startIcon={<FontAwesomeIcon icon={open ? faEyeSlash : faEye} style={{ paddingLeft: disableAccordion ? '28px' : undefined }} />}
            variant={open ? 'outlined' : 'contained'}
            disabled={disableAccordion}
            size="small"
            style={{
              pointerEvents: 'auto',
              whiteSpace: 'nowrap',
            }}
            onClick={(event) => {
              event.stopPropagation()
              setOpen(!open)
            }}
          >
            {!open ? t('pms.booking.reservation_new.additional_services.show') : t('pms.booking.reservation_new.additional_services.hide')}
            {disableAccordion && <div style={{ marginRight: '28px' }} />}
          </HicIconButton>
        </Stack>
      </AccordionSummary>
      <Box sx={{ m: 0, p: 1, backgroundColor: 'var(--AzureishWhite)' }}>
        <Stack direction="column" spacing={1}>
          {additionalServicesResult?.additionalServices?.map((additionalService) => {
            return (
              <div key={additionalService.id} style={{ backgroundColor: 'var(--White)' }}>
                <AdditionalServiceCell
                  disabled={loadingAdditionalServices}
                  additionalService={(() => {
                    const enabledAs = props.additionalServices.find((as) => as.additionalServiceId === additionalService.id)
                    if (enabledAs) return enabledAs
                    else {
                      return {
                        notCompatibleWithCart: true,
                        enabled: false,
                        additionalServiceId: additionalService.id,
                        priceZero: false,
                        additionalService: {
                          name: additionalService.name,
                          description: additionalService.description,
                          currency: additionalService.currency,
                          price: additionalService.price,
                          pricingType: additionalService.pricingType,
                          automaticForAdults: additionalService.automaticForAdults,
                          automaticForChildren: additionalService.automaticForChildren,
                          childServicePrices: additionalService.childServicePrices,
                        },
                      } as ReservationNewAdditionalService
                    }
                  })()}
                  cartsRow={cartRows}
                  onCartRowChanges={(rows) => updateCartRows(rows)}
                />
              </div>
            )
          })}
        </Stack>
      </Box>
    </Accordion>
  )
}

export default AdditionalServicesAccordion
